import { useState } from "react";
import { Icon } from "./icon";
import { cx } from "@/utils/cx";
import { initialsOf } from "@/utils/initials";

/**
 * Gymkee 2027 avatar (spec avatar 2.3; sheet DS35).
 *
 * A person is a round photo, or the initials in ink on paper muted when there is none; the photo
 * covers the circle. A business is the workspace variant: the same size, a square tile at radius
 * xs with a hairline; its mark is contained with a 4 inset on paper in both themes and never
 * cropped, because a mark may not be square and is drawn for a light ground; with no mark, its
 * initials. A picture that fails to load falls back to the initials rather than drawing a broken
 * image. No ring, no colour by role, no status dot: on a money page the avatar says who the
 * seller is and nothing else.
 */

export type AvatarVariant = "person" | "workspace";

export interface AvatarProps {
  /** The business or the person; the initials come from it. */
  name?: string;
  src?: string;
  /** Two of the seven avatar sizes; 48 is the header size (checkout-form 1.3). */
  size?: 40 | 48;
  variant?: AvatarVariant;
  className?: string;
}

/**
 * The initials are a fraction of the shape, never a text size that a reader setting scales: the
 * avatar table draws 14 at 40 and 15 at 48 (avatar 2.2, gate 2.2 exemption).
 */
const INITIAL_SIZE: Record<NonNullable<AvatarProps["size"]>, number> = {
  40: 14,
  48: 15,
};

export function Avatar({
  name = "",
  src,
  size = 48,
  variant = "person",
  className,
}: AvatarProps) {
  // The failure is remembered per picture, so a new src after a failed one is tried again.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showPhoto = !!src && failedSrc !== src;
  const initials = initialsOf(name);
  const workspace = variant === "workspace";

  return (
    <span
      className={cx(
        "ds-avatar",
        workspace && "ds-avatar-workspace",
        workspace && showPhoto && "ds-avatar-workspace-picture",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: INITIAL_SIZE[size],
        fontWeight: 600,
      }}
      aria-hidden={showPhoto ? undefined : "true"}
    >
      {showPhoto ? (
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          referrerPolicy="no-referrer"
          onError={() => setFailedSrc(src ?? null)}
          style={{
            width: "100%",
            height: "100%",
            // A person's photo covers the circle; a business mark is contained, never cropped (avatar 2.3).
            objectFit: workspace ? "contain" : "cover",
          }}
        />
      ) : initials ? (
        initials
      ) : (
        // A name with no letter (only an emoji) still draws a person, never an empty circle.
        <Icon name="user" size={size === 48 ? 24 : 20} />
      )}
    </span>
  );
}
